import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  input,
  output,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ProductsService } from 'src/app/services/product.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OnboardingStateService } from 'src/app/services/onboarding-state.service';
import { AuthService } from 'src/app/services/auth.service';
import { ACTIVITY_TYPE_LABELS } from 'src/app/models/onboarding.labels';
import { Product, ProductCategory } from 'src/app/models/product.model';
import type {
  ActivityType,
  ActivityTypeInfo,
} from 'src/app/models/cluster.model';
import { ClusterService } from 'src/app/services/cluster.service';
import { LogService } from '../../../services/log.service';
import { AnalyticsService } from '../../../services/analytics.service';

import { CatalogTemplatePickerComponent } from '../../../components/catalog-template-picker/catalog-template-picker.component';

@Component({
  selector: 'app-products-editor',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    CatalogTemplatePickerComponent,
  ],
  templateUrl: './products-editor.component.html',
  styleUrls: ['./products-editor.component.css'],
})
export class ProductsEditorComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  readonly productsChange = output<Product[]>();
  readonly productSelected = output<Product>();
  readonly closeCatalog = output<void>();
  readonly templateApplied = output<{
    templateName: string;
    productsCreated: number;
  }>();
  readonly showCatalogSearch = input(false);
  readonly products = input<Product[]>([]); // Synchronisation avec le parent
  readonly isOnboarding = input(false); // Mode onboarding = 7 cols max, sinon 8 cols
  readonly merchantType = input<string>('');

  // Stats optionnelles à afficher dans la section "Mes produits"
  readonly showStats = input(false);
  readonly validProducts = input(0);
  readonly totalProducts = input(0);
  readonly totalStockPurchaseValue = input(0);
  readonly totalStockSellValue = input(0);
  readonly productsAtRisk = input(0);
  readonly autoSaving = input(false);

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  activityType!: string;
  activityTypeLabels = ACTIVITY_TYPE_LABELS;

  /* PRODUITS */
  myProducts: Product[] = [];
  recommendedCatalog: Product[] = [];
  fullCatalog: Product[] = [];
  universalCatalog: Product[] = []; // Catalogue universel (tous les produits)
  filteredCatalog: Product[] = []; // Catalogue filtré localement (avec popularity)

  /* MODALES & AFFICHAGE */
  showRecommendedShowcase = false;
  showSearchCatalog = false;
  showCustomProductModal = false;
  showTemplatePicker = false;
  selectedProduct: Product | null = null;
  showExtendedCatalog = false; // Toggle catalogue étendu (universel)

  /* RECHERCHE & FILTRES */
  searchTerm = '';
  selectedCategory = 'all';
  showOnlyNew = false;
  isSearching = false;

  /* FILTRES MES PRODUITS */
  myProductsFilter = {
    search: '',
    popularity: 'all', // all, high, medium, low
    type: 'all', // all, custom, ai
    source: 'all', // all, core, extended, custom
    risk: 'all', // all, rupture, surstock
    sortBy: 'name', // name, price, popularity, margin
    sortOrder: 'asc', // asc, desc
  };
  showFilters = false;

  searchResults$ = this.productsService.searchResults$;

  /* NOTIFICATIONS */
  notification: { message: string; type: string } | null = null;

  /* PRODUIT PERSONNALISÉ */
  customProduct: {
    name: string;
    description: string;
    category: string;
    purchasePrice: number;
    sellPrice: number;
    quantity: number;
    unit: string;
    sku: string;
    productIcon: string;
    perishability: 'high' | 'medium' | 'low';
  } = {
    name: '',
    description: '',
    category: '',
    purchasePrice: 0,
    sellPrice: 0,
    quantity: 10,
    unit: 'unité',
    sku: '',
    productIcon: '📦',
    perishability: 'high',
  };

  /* ICÔNES & CATÉGORIES */
  productIcons = [
    '🥖',
    '🥐',
    '🍞',
    '🥯',
    '🧈',
    '🍫',
    '🍰',
    '🧁',
    '🎂',
    '🍪',
    '🥧',
    '🍩',
    '🍮',
    '🍯',
    '🥨',
    '🥞',
    '☕',
    '🍵',
    '🥤',
    '🧃',
    '🍋',
    '🍎',
    '🍇',
    '🍓',
    '🍒',
    '🍑',
    '🥝',
    '🍌',
    '🍉',
    '🍊',
    '🍕',
    '🍔',
    '🌭',
    '🥪',
    '🌮',
    '🌯',
    '🥗',
    '🍟',
    '🍗',
    '🥓',
    '🍖',
    '🥩',
    '🍤',
    '🦐',
    '🐟',
    '🍱',
    '🍙',
    '🍘',
    '🍚',
    '🍛',
  ];

  /* METADONNÉES DYNAMIQUES */
  categories: ProductCategory[] = [];
  units: string[] = [];
  activityTypes: ActivityTypeInfo[] = [];

  constructor(
    private router: Router,
    private onboardingStateService: OnboardingStateService,
    private productsService: ProductsService,
    private clusterService: ClusterService,
    private authService: AuthService,
  ) {
    // Synchroniser myProducts quand le parent met à jour products
    effect(() => {
      const prods = this.products();
      if (prods.length > 0) {
        this.myProducts = [...prods];
      }
    });
  }

  ngOnInit(): void {
    // Charger les métadonnées depuis le backend
    this.productsService
      .loadMeta()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Lecture directe des signaux (compatible zoneless)
        this.categories = this.productsService.categories();
        this.units = this.productsService.units();
        this.activityTypes = this.productsService.activityTypes();
        this.cdr.markForCheck();
      });

    // Récupérer activityType AVANT de charger le catalogue
    // D'abord essayer depuis l'onboarding state (mode onboarding)
    const data = this.onboardingStateService.getFormData();
    if (data?.activityType) {
      this.activityType = data.activityType as string;
    } else {
      // Sinon récupérer depuis le user (mode normal post-onboarding)
      const user = this.authService.getCurrentUser();
      this.activityType = user?.activityType || 'bakery';
      this.log.debug(
        '📦 ActivityType récupéré depuis user:',
        this.activityType,
      );
    }
    this.loadRecommendedCatalog();

    this.searchResults$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isSearching = false;
        this.cdr.markForCheck();
      });

    // Ne charger depuis l'API que si aucun produit n'est fourni via input()
    if (this.products().length === 0) {
      this.loadProducts();
    } else {
      this.myProducts = [...this.products()];
    }
  }

  private emitProducts() {
    this.productsChange.emit(this.myProducts);
  }

  /* ==================== PRODUITS ==================== */
  loadProducts() {
    // charger les produits pré-existants
    this.productsService.getMyProducts().subscribe((products) => {
      this.myProducts = products;
      this.cdr.markForCheck();
    });
  }

  loadRecommendedCatalog() {
    if (!this.activityType) return;

    this.clusterService
      .getGenericCluster(this.activityType as ActivityType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cluster) => {
          const overrides = cluster.catalogueOverrides || [];

          // Mapper les overrides vers le format Product
          const mapToProduct = (o: any): Product =>
            ({
              name: o.name || o.sku,
              sku: o.sku,
              category: o.category || '',
              unit: o.unit || 'unité',
              defaultPurchasePrice: o.basePurchasePrice,
              defaultSellPrice: o.baseSellPrice,
              defaultQuantity: o.baseQuantity,
              purchasePrice: o.basePurchasePrice,
              sellPrice: o.baseSellPrice,
              quantity: o.baseQuantity,
              popularity: o.popularity,
              isRecommended: o.isRecommended,
              productIcon: o.productIcon || '📦',
              sourceCatalogProductId: o.catalogProductId || null,
            }) as Product;

          // Recommandés : isRecommended || popularité >= 8 (échelle 0-10)
          this.recommendedCatalog = overrides
            .filter((o: any) => o.isRecommended || o.popularity >= 8)
            .sort((a: any, b: any) => b.popularity - a.popularity)
            .map(mapToProduct);

          // Catalogue complet
          this.fullCatalog = overrides
            .sort((a: any, b: any) => b.popularity - a.popularity)
            .map(mapToProduct);

          // Initialiser le catalogue filtré avec tous les produits
          this.filteredCatalog = [...this.fullCatalog];

          this.log.debug(
            `📦 Catalogue chargé: ${this.recommendedCatalog.length} recommandés, ${this.fullCatalog.length} total`,
            '\n📊 Popularités recommandés:',
            this.recommendedCatalog.map((p) => ({
              name: p.name,
              pop: p.popularity,
            })),
          );
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.error('❌ Erreur chargement catalogue:', err);
          // Fallback sur les anciens endpoints
          this.loadRecommendedCatalogLegacy();
          this.loadFullCatalogLegacy();
        },
      });
  }

  /** Fallback — ancien endpoint */
  private loadRecommendedCatalogLegacy() {
    this.productsService.getRecommendedCatalog().subscribe((products) => {
      this.recommendedCatalog = (products ?? []).sort((a, b) => {
        const popularityCompare = (b.popularity ?? 0) - (a.popularity ?? 0);
        if (popularityCompare !== 0) return popularityCompare;
        return (a.category ?? '').localeCompare(b.category ?? '');
      });
      this.cdr.markForCheck();
    });
  }

  /** Fallback — ancien endpoint */
  private loadFullCatalogLegacy() {
    this.productsService.getCatalog().subscribe((products) => {
      this.fullCatalog = products ?? [];
      this.cdr.markForCheck();
    });
  }

  /**
   * Réagit au changement du toggle catalogue étendu
   * Note: showExtendedCatalog est déjà mis à jour par ngModel
   */
  toggleExtendedCatalog() {
    // Ne pas re-toggle - ngModel l'a déjà fait

    if (this.showExtendedCatalog) {
      // Charger le catalogue universel si pas encore fait
      if (this.universalCatalog.length === 0) {
        this.isSearching = true;
        this.clusterService.getUniversalCatalogue().subscribe({
          next: (products) => {
            this.universalCatalog = products.map(
              (o: any) =>
                ({
                  name: o.name || o.sku,
                  sku: o.sku,
                  category: o.category || '',
                  unit: o.unit || 'unité',
                  defaultPurchasePrice: o.basePurchasePrice,
                  defaultSellPrice: o.baseSellPrice,
                  defaultQuantity: o.baseQuantity,
                  purchasePrice: o.basePurchasePrice,
                  sellPrice: o.baseSellPrice,
                  quantity: o.baseQuantity,
                  popularity: o.popularity,
                  isRecommended: o.isRecommended,
                  productIcon: o.productIcon || '📦',
                  sourceCatalogProductId: o.catalogProductId || null,
                }) as Product,
            );
            this.filteredCatalog = [...this.universalCatalog];
            this.isSearching = false;
            this.log.debug(
              `🌍 Catalogue universel chargé: ${this.universalCatalog.length} produits`,
            );
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.log.error('❌ Erreur chargement catalogue universel:', err);
            this.isSearching = false;
            this.cdr.markForCheck();
          },
        });
      } else {
        this.filteredCatalog = [...this.universalCatalog];
      }
    } else {
      // Revenir au catalogue d'activité
      this.filteredCatalog = [...this.fullCatalog];
    }

    // Réappliquer les filtres
    this.filterCatalogLocally();
  }

  /**
   * Détermine le sourceType d'un produit en fonction du catalogue source
   * - Si le toggle "catalogue étendu" est actif ET le produit n'est pas dans fullCatalog → 'extended'
   * - Sinon → 'core'
   */
  private getSourceTypeForProduct(
    catalogProduct: Product,
  ): 'core' | 'extended' | 'custom' {
    // Si on est en mode catalogue étendu, vérifier si le produit est dans le cluster d'activité
    if (this.showExtendedCatalog) {
      const clusterSkus = new Set(this.fullCatalog.map((p) => p.sku));
      const isInClusterCatalog = clusterSkus.has(catalogProduct.sku);
      this.log.debug(
        `🏷️ sourceType pour ${catalogProduct.name}: sku=${catalogProduct.sku}, dansCluster=${isInClusterCatalog}, fullCatalog.length=${this.fullCatalog.length}`,
      );
      return isInClusterCatalog ? 'core' : 'extended';
    }
    // Si on est en mode catalogue d'activité, c'est forcément du cœur de métier
    return 'core';
  }

  /**
   * Retourne le label pour le sourceType
   */
  getSourceTypeLabel(sourceType?: 'core' | 'extended' | 'custom'): string {
    switch (sourceType) {
      case 'core':
        return 'Cœur';
      case 'extended':
        return 'Étendu';
      case 'custom':
        return 'Perso';
      default:
        return '';
    }
  }

  /**
   * Retourne l'icône pour le sourceType
   */
  getSourceTypeIcon(sourceType?: 'core' | 'extended' | 'custom'): string {
    switch (sourceType) {
      case 'core':
        return '🎯';
      case 'extended':
        return '🌍';
      case 'custom':
        return '✨';
      default:
        return '';
    }
  }

  /* ==================== GESTION PRODUITS ==================== */

  /**
   * Ajoute tous les produits recommandés qui ne sont pas encore ajoutés
   */
  addAllRecommendedProducts() {
    const productsToAdd = this.recommendedCatalog.filter(
      (p) => !this.isProductAdded(p.name),
    );

    if (productsToAdd.length === 0) {
      this.showNotification('Tous les produits sont déjà ajoutés', 'info');
      return;
    }

    productsToAdd.forEach((catalogProduct) => {
      const newProduct: Product = {
        ...catalogProduct,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        quantity: Number(catalogProduct.defaultQuantity ?? 0),
        sellPrice: Number(catalogProduct.defaultSellPrice ?? 0),
        purchasePrice: Number(catalogProduct.defaultPurchasePrice ?? 0),
        sourceType: 'core', // Les produits recommandés sont toujours du cœur de métier
      };
      this.myProducts.push(newProduct);
    });

    this.emitProducts();
    this.analytics.trackProduitAjoute(`bulk_${productsToAdd.length}`);
    this.showNotification(
      `${productsToAdd.length} produits ajoutés !`,
      'success',
    );
  }

  /**
   * Retourne le nombre de produits recommandés non encore ajoutés
   */
  getNotAddedRecommendedCount(): number {
    return this.recommendedCatalog.filter((p) => !this.isProductAdded(p.name))
      .length;
  }

  addProductFromCatalog(catalogProduct: Product) {
    if (this.isProductAdded(catalogProduct.name)) {
      return;
    }

    const sourceType = this.getSourceTypeForProduct(catalogProduct);

    this.log.debug(
      `➕ Ajout produit: ${catalogProduct.name}, SKU=${catalogProduct.sku}, showExtended=${this.showExtendedCatalog}, sourceType=${sourceType}`,
    );

    const newProduct: Product = {
      ...catalogProduct,
      id: Date.now().toString(),
      quantity: Number(catalogProduct.defaultQuantity ?? 0),
      sellPrice: Number(catalogProduct.defaultSellPrice ?? 0),
      purchasePrice: Number(catalogProduct.defaultPurchasePrice ?? 0),
      sourceType,
    };

    this.myProducts.push(newProduct);
    this.emitProducts();
    this.analytics.trackProduitAjoute(catalogProduct.category);

    const sourceLabel =
      sourceType === 'core' ? '' : ` (${this.getSourceTypeLabel(sourceType)})`;
    this.showNotification(
      `"${catalogProduct.name}"${sourceLabel} ajouté !`,
      'success',
    );
  }

  removeProductByName(name: string) {
    const product = this.myProducts.find((p) => p.name === name);
    if (product) this.removeProduct(product.id);
  }

  removeProduct(id: string) {
    const product = this.myProducts.find((p) => p.id === id);
    this.myProducts = this.myProducts.filter((p) => p.id !== id);
    this.emitProducts();

    if (product) {
      this.analytics.trackProduitSupprime(product.name);
      this.showNotification(`"${product.name}" supprimé`, 'info');
    }
  }

  confirmingDeleteAll = false;

  deleteAllProducts() {
    this.confirmingDeleteAll = true;
  }

  confirmDeleteAll() {
    this.confirmingDeleteAll = false;
    this.myProducts = [];
    this.emitProducts();
    this.showNotification('Tous les produits ont été supprimés', 'info');
  }

  cancelDeleteAll() {
    this.confirmingDeleteAll = false;
  }

  onTemplateApplied(event: { templateName: string; productsCreated: number }) {
    this.showTemplatePicker = false;
    this.showNotification(
      `✅ Template "${event.templateName}" appliqué (${event.productsCreated} produits)`,
      'success',
    );
    // Notify parent to reload products from backend
    this.templateApplied.emit(event);
  }

  isProductAdded(name: string) {
    return this.myProducts.some((p) => p.name === name);
  }

  selectProduct(product: Product) {
    this.selectedProduct = product;
    this.productSelected.emit(product);
  }

  getMargin(product: Product): number {
    if (!product.purchasePrice || product.purchasePrice === 0) return 0;
    return Math.round(
      ((product.sellPrice - product.purchasePrice) / product.purchasePrice) *
        100,
    );
  }

  /** Cache pour getSuggestions — évite de recréer des tableaux à chaque CD */
  private suggestionsCache = new Map<
    string,
    { type: string; icon: string; message: string; tip: string }[]
  >();

  /**
   * Retourne les suggestions IA pour un produit
   */
  getSuggestions(
    product: Product,
  ): { type: string; icon: string; message: string; tip: string }[] {
    const suggestions: {
      type: string;
      icon: string;
      message: string;
      tip: string;
    }[] = [];
    const cacheKey = `${product.id}-${product.quantity}-${product.purchasePrice}-${product.sellPrice}`;
    if (this.suggestionsCache.has(cacheKey)) {
      return this.suggestionsCache.get(cacheKey)!;
    }
    const margin = this.getMargin(product);

    // Rupture de stock (priorité haute)
    if (product.quantity === 0) {
      suggestions.push({
        type: 'danger',
        icon: '🚫',
        message: 'Rupture de stock !',
        tip: 'Ce produit ne peut plus être vendu. Réapprovisionnez rapidement.',
      });
    }

    // Stock critique (< 3)
    if (product.quantity > 0 && product.quantity <= 3) {
      suggestions.push({
        type: 'danger',
        icon: '📦',
        message: `Stock critique (${product.quantity} restant${product.quantity > 1 ? 's' : ''})`,
        tip: 'Commandez dès maintenant pour éviter la rupture',
      });
    }

    // Stock faible (3-5)
    if (product.quantity > 3 && product.quantity <= 5) {
      suggestions.push({
        type: 'warning',
        icon: '📦',
        message: `Stock bas (${product.quantity} restants)`,
        tip: 'Pensez à réapprovisionner cette semaine',
      });
    }

    // Marge négative ou nulle
    if (margin <= 0) {
      suggestions.push({
        type: 'danger',
        icon: '💸',
        message: margin < 0 ? `Vente à perte (${margin}%)` : 'Aucune marge',
        tip: 'Augmentez le prix de vente immédiatement !',
      });
    }

    // Marge très faible (1-20%)
    if (margin > 0 && margin < 20) {
      const suggestedPrice = +(product.purchasePrice * 1.4).toFixed(2);
      suggestions.push({
        type: 'warning',
        icon: '⚠️',
        message: `Marge insuffisante (${margin}%)`,
        tip: `Recommandé: ${suggestedPrice}€ pour 40% de marge`,
      });
    }

    // Marge faible (20-30%)
    if (margin >= 20 && margin < 30) {
      const suggestedPrice = +(product.purchasePrice * 1.5).toFixed(2);
      suggestions.push({
        type: 'info',
        icon: '💡',
        message: `Marge correcte mais améliorable (${margin}%)`,
        tip: `Objectif: ${suggestedPrice}€ pour 50% de marge`,
      });
    }

    // Popularité très faible avec stock élevé
    if (
      product.popularity &&
      product.popularity <= 2 &&
      product.quantity > 10
    ) {
      suggestions.push({
        type: 'warning',
        icon: '📊',
        message: 'Produit peu vendu avec stock élevé',
        tip: 'Envisagez une promo ou mettez-le en avant',
      });
    }

    // Popularité faible générale
    if (
      product.popularity &&
      product.popularity <= 2 &&
      product.quantity <= 10
    ) {
      suggestions.push({
        type: 'info',
        icon: '📊',
        message: 'Popularité faible',
        tip: 'Testez une mise en avant ou ajustez le prix',
      });
    }

    // Prix de vente non défini
    if (!product.sellPrice || product.sellPrice === 0) {
      suggestions.push({
        type: 'danger',
        icon: '🏷️',
        message: 'Prix de vente non défini',
        tip: 'Définissez un prix pour pouvoir vendre ce produit',
      });
    }

    // Prix d'achat non défini
    if (!product.purchasePrice || product.purchasePrice === 0) {
      suggestions.push({
        type: 'warning',
        icon: '🏷️',
        message: "Prix d'achat non renseigné",
        tip: 'Renseignez-le pour calculer votre marge',
      });
    }

    this.suggestionsCache.set(cacheKey, suggestions);
    return suggestions;
  }
  hasSuggestions(product: Product): boolean {
    return this.getSuggestions(product).length > 0;
  }

  /* ==================== STATS SOURCE TYPE ==================== */

  /** Nombre de produits cœur de métier */
  get coreProductsCount(): number {
    return this.myProducts.filter(
      (p) => p.sourceType === 'core' || !p.sourceType,
    ).length;
  }

  /** Nombre de produits du catalogue étendu */
  get extendedProductsCount(): number {
    return this.myProducts.filter((p) => p.sourceType === 'extended').length;
  }

  /** Nombre de produits personnalisés */
  get customProductsCount(): number {
    return this.myProducts.filter(
      (p) => p.sourceType === 'custom' || p.isCustom,
    ).length;
  }

  /* ==================== FILTRES MES PRODUITS ==================== */
  get filteredMyProducts(): Product[] {
    let filtered = [...this.myProducts];

    // Filtre par recherche
    if (this.myProductsFilter.search) {
      const search = this.myProductsFilter.search.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(search) ||
          p.category?.toLowerCase().includes(search),
      );
    }

    // Filtre par popularité
    if (this.myProductsFilter.popularity !== 'all') {
      filtered = filtered.filter((p) => {
        const pop = p.popularity || 0;
        switch (this.myProductsFilter.popularity) {
          case 'high':
            return pop >= 9;
          case 'medium':
            return pop >= 5 && pop < 9;
          case 'low':
            return pop < 5;
          default:
            return true;
        }
      });
    }

    // Filtre par type
    if (this.myProductsFilter.type !== 'all') {
      filtered = filtered.filter((p) => {
        switch (this.myProductsFilter.type) {
          case 'custom':
            return p.isCustom;
          case 'ai':
            return p.isAISuggested;
          default:
            return true;
        }
      });
    }

    // Filtre par source (core, extended, custom)
    if (this.myProductsFilter.source !== 'all') {
      filtered = filtered.filter(
        (p) => p.sourceType === this.myProductsFilter.source,
      );
    }

    // Filtre par risque
    if (this.myProductsFilter.risk !== 'all') {
      filtered = filtered.filter((p) => {
        switch (this.myProductsFilter.risk) {
          case 'rupture':
            return p.riskType === 'rupture';
          case 'surstock':
            return p.riskType === 'surstock';
          case 'any':
            return p.riskType === 'rupture' || p.riskType === 'surstock';
          default:
            return true;
        }
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (this.myProductsFilter.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'price':
          comparison = (a.sellPrice || 0) - (b.sellPrice || 0);
          break;
        case 'popularity':
          comparison = (b.popularity || 0) - (a.popularity || 0);
          break;
        case 'margin':
          comparison = this.getMargin(b) - this.getMargin(a);
          break;
      }
      return this.myProductsFilter.sortOrder === 'asc'
        ? comparison
        : -comparison;
    });

    return filtered;
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  resetMyProductsFilters() {
    this.myProductsFilter = {
      search: '',
      popularity: 'all',
      type: 'all',
      source: 'all',
      risk: 'all',
      sortBy: 'name',
      sortOrder: 'asc',
    };
  }

  get hasActiveFilters(): boolean {
    return (
      this.myProductsFilter.search !== '' ||
      this.myProductsFilter.popularity !== 'all' ||
      this.myProductsFilter.type !== 'all' ||
      this.myProductsFilter.source !== 'all' ||
      this.myProductsFilter.risk !== 'all'
    );
  }

  trackByProductId(index: number, product: Product): string {
    return product.id;
  }

  /* ==================== RECHERCHE ==================== */
  onSearchChange(term: string) {
    this.searchTerm = term;
    this.filterCatalogLocally();
  }

  onCategoryChange(category: string) {
    this.selectedCategory = category;
    this.filterCatalogLocally();
  }

  /** Filtre le catalogue localement (conserve la popularity) */
  private filterCatalogLocally() {
    // Choisir le bon catalogue source selon le toggle
    const sourceCatalog = this.showExtendedCatalog
      ? this.universalCatalog
      : this.fullCatalog;

    let filtered = [...sourceCatalog];

    // Filtre par terme de recherche
    if (this.searchTerm && this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (p) =>
          p.name?.toLowerCase().includes(term) ||
          p.category?.toLowerCase().includes(term) ||
          p.sku?.toLowerCase().includes(term),
      );
    }

    // Filtre par catégorie
    if (this.selectedCategory && this.selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === this.selectedCategory);
    }

    this.filteredCatalog = filtered;
  }

  onNewFilterChange(isNew: boolean) {
    this.showOnlyNew = isNew;
    this.filterCatalogLocally();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCategory = 'all';
    this.showOnlyNew = false;
    // Réinitialiser avec le bon catalogue source
    this.filteredCatalog = this.showExtendedCatalog
      ? [...this.universalCatalog]
      : [...this.fullCatalog];
  }

  toggleSearchCatalog() {
    this.showSearchCatalog = !this.showSearchCatalog;
    if (this.showSearchCatalog) this.resetFilters();
  }

  /* ==================== PRODUIT PERSONNALISÉ ==================== */
  openAddCustomProductModal() {
    this.showCustomProductModal = true;
    this.resetCustomProduct();
  }

  closeAddCustomProductModal() {
    this.showCustomProductModal = false;
    this.resetCustomProduct();
  }

  resetCustomProduct() {
    this.customProduct = {
      name: '',
      description: '',
      category: this.categories[0]?.name ?? '',
      purchasePrice: 0,
      sellPrice: 0,
      quantity: 10,
      unit: 'unité',
      sku: '',
      productIcon: '📦',
      perishability: 'high' as 'high' | 'medium' | 'low',
    };
  }

  isCustomProductValid(): boolean {
    return !!(
      this.customProduct.name &&
      this.customProduct.category &&
      this.customProduct.quantity > 0 &&
      this.customProduct.purchasePrice >= 0
    );
  }

  addCustomProduct() {
    if (!this.isCustomProductValid()) return;

    const now = new Date();

    // Générer un SKU automatique si vide
    const generatedSku =
      this.customProduct.sku?.trim() ||
      `CUSTOM-${this.customProduct.name.substring(0, 3).toUpperCase()}-${Date.now().toString().slice(-6)}`;

    const newProduct: Product = {
      id: Date.now().toString(),
      name: this.customProduct.name,
      description: this.customProduct.description ?? '',
      category: this.customProduct.category,
      purchasePrice: this.customProduct.purchasePrice,
      sellPrice:
        this.customProduct.sellPrice ||
        +(this.customProduct.purchasePrice * 1.6).toFixed(2),
      quantity: this.customProduct.quantity,
      unit: this.customProduct.unit,
      sku: generatedSku,
      productIcon: this.customProduct.productIcon,
      popularity: 0,
      isCustom: true,
      isRecommended: false,
      sourceType: 'custom',
      perishability: this.customProduct.perishability || 'high',
      createdAt: now,
      updatedAt: now,
    };

    this.myProducts.push(newProduct);
    this.emitProducts();

    this.showNotification(
      `"${newProduct.name}" ajouté avec succès !`,
      'success',
    );

    this.closeAddCustomProductModal();
  }

  showNotification(message: string, type: string) {
    this.notification = { message, type };
    setTimeout(() => (this.notification = null), 3000);
  }

  getTotalValue() {
    return this.myProducts.reduce(
      (sum, p) => sum + (p.purchasePrice || 0) * p.quantity,
      0,
    );
  }

  getTotalQuantity() {
    return this.myProducts.reduce((sum, p) => sum + p.quantity, 0);
  }

  getUniqueCategories() {
    return new Set(this.myProducts.map((p) => p.category)).size;
  }

  saveProducts() {
    this.productsService.saveMyProducts(this.myProducts).subscribe({
      next: () => {
        this.showNotification('✅ Produits enregistrés !', 'success');
        setTimeout(() => this.router.navigate(['/dashboard']), 1500);
        this.cdr.markForCheck();
      },
      error: () => {
        this.showNotification('❌ Erreur lors de la sauvegarde', 'error');
        this.cdr.markForCheck();
      },
    });
  }

  cancel() {
    this.router.navigate(['/dashboard']);
  }
}
